# 完整的集团审核页面内容
<template>
  <div class="group-page">
    <!-- 搜索区域 -->
    <v-card class="search-card">
      <v-card-text>
        <v-row align="center">
          <v-col cols="12" sm="3">
            <v-select
              v-model="searchForm.groupReviewStatus"
              :items="statusOptions"
              item-title="label"
              item-value="value"
              label="审核状态"
              hide-details
              density="compact"
              variant="outlined"
              bg-color="#2B2D33"
              color="primary"
              class="search-input"
            />
          </v-col>
          <v-col cols="12" sm="3">
            <v-text-field
              v-model="searchForm.submitReviewTimeStart"
              label="开始时间"
              type="date"
              hide-details
              density="compact"
              variant="outlined"
              bg-color="#2B2D33"
              color="primary"
              class="search-input date-input"
              style="max-width: 180px;"
            />
          </v-col>
          <v-col cols="12" sm="3">
            <v-text-field
              v-model="searchForm.submitReviewTimeEnd"
              label="结束时间"
              type="date"
              hide-details
              density="compact"
              variant="outlined"
              bg-color="#2B2D33"
              color="primary"
              class="search-input date-input"
              style="max-width: 180px;"
            />
          </v-col>
          <v-col cols="12" sm="3" class="d-flex align-center">
            <v-btn
              color="primary"
              class="search-btn mr-2"
              variant="tonal"
              @click="handleSearch"
            >
              查询
            </v-btn>
            <v-btn
              variant="tonal"
              class="search-btn"
              color="info"
              @click="handleReset"
            >
              重置
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- 表格区域 -->
    <v-card class="table-card">
      <v-card-text class="pa-0">
        <v-data-table
          v-model:page="page"
          :headers="headers"
          :items="tableData"
          :items-per-page="pageSize"
          :loading="loading"
          :total-items="total"
          class="group-table"
          hover
          :no-data-text="'没有待处理的内容'"
        >
          <template #item="{ item }">
            <tr>
              <td class="text-start">{{ item.so }}</td>
              <td class="text-start customer-name-cell">{{ item.customerName }}</td>
              <td class="text-start">{{ item.no }}</td>
              <td class="text-start">{{ item.batch }}</td>
              <td class="text-start">{{ item.name }}</td>
              <td class="text-center">{{ item.count }}</td>
              <td class="text-center">{{ item.submitReviewTimeText }}</td>
              <td class="text-center">
                <v-chip
                  :color="getStatusColor(item.financeReviewStatus, 'finance')"
                  size="small"
                >
                  {{ item.financeReviewStatusText }}
                </v-chip>
              </td>
              <td class="text-center">
                <v-chip
                  :color="getStatusColor(item.groupReviewStatus, 'group')"
                  size="small"
                >
                  {{ item.groupReviewStatusText }}
                </v-chip>
              </td>
              <td class="text-center">
                <v-btn
                  color="primary"
                  size="small"
                  variant="flat"
                  density="comfortable"
                  class="text-none font-weight-regular view-drawings-btn"
                  @click="handleViewDrawings(item)"
                >
                  查看图纸
                </v-btn>
              </td>
              <td class="text-center">
                <v-btn
                  v-if="item.groupReviewStatus === GROUP_REVIEW_STATUS.PENDING && hasPermission('jf_group')"
                  color="primary"
                  size="small"
                  density="comfortable"
                  class="text-none font-weight-regular review-btn"
                  @click="handleReview(item)"
                >
                  审核
                </v-btn>
                <span
                  v-else-if="item.groupReviewStatus === GROUP_REVIEW_STATUS.APPROVED"
                  class="status-text"
                >
                  已通过
                </span>
                <span
                  v-else-if="item.groupReviewStatus === GROUP_REVIEW_STATUS.REJECTED"
                  class="status-text"
                >
                  已拒绝
                </span>
                <v-btn
                  v-else
                  color="info"
                  size="small"
                  variant="tonal"
                  density="comfortable"
                  class="text-none font-weight-regular"
                  @click="handleViewDetail(item)"
                >
                  查看
                </v-btn>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- 审核弹窗 -->
    <v-dialog v-model="reviewDialog" max-width="400">
      <v-card class="review-dialog">
        <v-card-title class="review-title">审核确认</v-card-title>
        <v-card-text class="review-content">
          <div class="review-buttons">
            <v-btn
              class="review-action-btn approve-btn"
              :loading="submitting"
              @click="handleConfirmReview(1)"
            >
              通过
            </v-btn>
            <v-btn
              class="review-action-btn reject-btn"
              :loading="submitting"
              @click="handleConfirmReview(2)"
            >
              拒绝
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { groupApi, GROUP_REVIEW_STATUS, GROUP_REVIEW_STATUS_TEXT, FINANCE_REVIEW_STATUS, FINANCE_REVIEW_STATUS_TEXT } from '@/services/api'
import type { GroupReviewItem } from '@/services/api'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/stores/user'

const router = useRouter()
const userStore = useUserStore()
const hasPermission = (permission: string) => userStore.hasPermission(permission)

// 表格列定义
const headers = [
  { title: '合同编号', key: 'so', align: 'start' as 'start', sortable: false },
  { title: '客户名称', key: 'customerName', align: 'start' as 'start', sortable: false },
  { title: '产品编号', key: 'no', align: 'start' as 'start', sortable: false },
  { title: '批号', key: 'batch', align: 'start' as 'start', sortable: false },
  { title: '产品名称', key: 'name', align: 'start' as 'start', sortable: false },
  { title: '数量', key: 'count', align: 'center' as 'center', sortable: false },
  { title: '提交审核时间', key: 'submitReviewTimeText', align: 'center' as 'center', sortable: false },
  { title: '财务审核', key: 'financeReviewStatusText', align: 'center' as 'center', sortable: false },
  { title: '集团审核', key: 'groupReviewStatusText', align: 'center' as 'center', sortable: false },
  { title: '图纸', key: 'drawings', align: 'center' as 'center', sortable: false },
  { title: '操作', key: 'actions', align: 'center' as 'center', sortable: false }
]

// 状态选项
const statusOptions = [
  { label: '待审核', value: GROUP_REVIEW_STATUS.PENDING },
  { label: '已通过', value: GROUP_REVIEW_STATUS.APPROVED },
  { label: '已拒绝', value: GROUP_REVIEW_STATUS.REJECTED }
]

// 搜索表单
const searchForm = ref({
  groupReviewStatus: GROUP_REVIEW_STATUS.PENDING,
  submitReviewTimeStart: '',
  submitReviewTimeEnd: ''
})

// 分页相关
const page = ref(1)
const pageSize = ref(10)
const total = ref(0)

// 表格数据
const loading = ref(false)
const tableData = ref<GroupReviewItem[]>([])

// 获取表格数据
const fetchData = async () => {
  loading.value = true
  try {
    // 构建请求参数
    const params: any = {
      page: page.value,
      pageSize: pageSize.value,
      groupReviewStatus: searchForm.value.groupReviewStatus
    }
    
    // 添加时间参数
    if (searchForm.value.submitReviewTimeStart) {
      params.submitReviewTimeStart = `${searchForm.value.submitReviewTimeStart} 00:00:01`
    }
    
    if (searchForm.value.submitReviewTimeEnd) {
      params.submitReviewTimeEnd = `${searchForm.value.submitReviewTimeEnd} 23:59:59`
    }
    
    const res = await groupApi.getGroupReviewList(params)
    if (res.code === 0 && res.data) {
      tableData.value = res.data.list
      total.value = res.data.total
    } else {
      tableData.value = []
      total.value = 0
    }
  } catch (error) {
    console.error('获取列表失败:', error)
    tableData.value = []
    total.value = 0
  } finally {
    loading.value = false
  }
}

// 搜索
const handleSearch = () => {
  page.value = 1
  fetchData()
}

// 重置
const handleReset = () => {
  searchForm.value = {
    groupReviewStatus: GROUP_REVIEW_STATUS.PENDING,
    submitReviewTimeStart: getFirstDayOfMonth(),
    submitReviewTimeEnd: getCurrentDate()
  }
  page.value = 1
  fetchData()
}

// 获取状态颜色
const getStatusColor = (status: number, type: 'finance' | 'group' = 'group') => {
  if (type === 'finance') {
    switch (status) {
      case FINANCE_REVIEW_STATUS.PENDING: return 'warning'  // 待审核
      case FINANCE_REVIEW_STATUS.APPROVED: return 'success' // 已通过
      case FINANCE_REVIEW_STATUS.REJECTED: return 'error'   // 已拒绝
      default: return 'default'
    }
  } else {
    switch (status) {
      case GROUP_REVIEW_STATUS.PENDING: return 'warning'
      case GROUP_REVIEW_STATUS.APPROVED: return 'success'
      case GROUP_REVIEW_STATUS.REJECTED: return 'error'
      default: return 'default'
    }
  }
}

// 审核相关
const reviewDialog = ref(false)
const submitting = ref(false)
const currentReviewItem = ref<GroupReviewItem | null>(null)

// 打开审核弹窗
const handleReview = (item: GroupReviewItem) => {
  currentReviewItem.value = item
  reviewDialog.value = true
}

// 确认审核
const handleConfirmReview = async (status: number) => {
  if (!currentReviewItem.value) return
  
  submitting.value = true
  try {
    console.log('提交审核参数:', {
      id: currentReviewItem.value.id,
      groupReviewStatus: status
    })
    const res = await groupApi.submitGroupReview({
      id: currentReviewItem.value.id,
      groupReviewStatus: status
    })
    if (res.code === 0) {
      reviewDialog.value = false
      fetchData()
    }
  } catch (error) {
    console.error('提交审核失败:', error)
  } finally {
    submitting.value = false
  }
}

// 查看图纸
const handleViewDrawings = (item: GroupReviewItem) => {
  const contractId = item.productOrderId
  const productId = item.id
  console.log('跳转参数:', {
    contractId,
    productId,
    item
  })
  if (!contractId) {
    console.error('合同编号不存在:', item)
    return
  }
  router.push(`/contract/${contractId}/product/${productId}/images`)
}

// 格式化日期为 YYYY-MM-DD
const formatDate = (date: Date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

// 获取当月第一天
const getFirstDayOfMonth = () => {
  const date = new Date()
  date.setDate(1)
  return formatDate(date)
}

// 获取当前日期
const getCurrentDate = () => {
  return formatDate(new Date())
}

// 初始化日期
const initDates = () => {
  searchForm.value.submitReviewTimeStart = getFirstDayOfMonth()
  searchForm.value.submitReviewTimeEnd = getCurrentDate()
}

// 初始化
onMounted(() => {
  initDates()
  fetchData()
})

// 新增的审计方法
const handleAudit = () => {
  // 实现审计逻辑
}

// 修改 handleViewDetail 函数为 handleViewDrawings
const handleViewDetail = (item: GroupReviewItem) => {
  handleViewDrawings(item)
}
</script>

<style lang="scss" scoped>
.group-page {
  height: 100%;
  background-color: #2B2D33;
  display: flex;
  flex-direction: column;
}

.search-card {
  background-color: #2B2D33 !important;
  border: none !important;
  margin-bottom: 8px;

  :deep(.v-card-text) {
    padding: 16px 0;
  }

  :deep(.search-input) {
    .v-field__input,
    .v-field__field,
    input,
    .v-select__selection-text {
      color: #fff !important;
    }

    .v-field__outline {
      color: rgba(255, 255, 255, 0.12) !important;
    }

    .v-field__input::placeholder {
      color: rgba(255, 255, 255, 0.7) !important;
    }

    .v-label {
      color: rgba(255, 255, 255, 0.7) !important;
    }

    .v-select__menu {
      .v-list {
        background-color: #2B2D33 !important;
        
        .v-list-item {
          color: #fff !important;
          
          &:hover {
            background-color: rgba(255, 255, 255, 0.04) !important;
          }
        }
      }
    }
  }

  .search-btn {
    height: 36px;
    min-width: 90px;
    font-weight: 400;
    text-transform: none;
    letter-spacing: 0;
  }
}

.table-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: #2B2D33 !important;
  border-radius: 0;
  
  :deep(.v-card-text) {
    flex: 1;
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  :deep(.view-drawings-btn) {
    color: #fff !important;
    
    &:hover {
      opacity: 0.8;
    }
  }

  :deep(.review-btn) {
    background-color: rgb(var(--v-theme-primary)) !important;
    color: #fff !important;
    font-weight: 500 !important;
    
    &:hover {
      opacity: 0.9;
    }
  }

  :deep(.customer-name-cell) {
    max-width: 120px;
    white-space: pre-wrap !important;
    word-break: break-all;
    line-height: 1.2;
    padding: 8px 16px !important;
  }
  
  .status-text {
    color: rgba(255, 255, 255, 0.7);
    font-size: 14px;
  }
}

:deep(.group-table) {
  flex: 1;
  height: 100%;
  background-color: #2B2D33 !important;
  color: #fff !important;

  .v-data-table-header {
    th {
      color: rgba(255, 255, 255, 0.7) !important;
      font-weight: 400;
      font-size: 14px;
      background-color: #2B2D33 !important;
      border-bottom: thin solid rgba(255, 255, 255, 0.12);
      padding: 0 16px !important;
    }
  }

  .v-data-table__tr {
    color: #fff !important;
    
    td {
      color: #fff !important;
    }

    &:hover td {
      background-color: rgba(255, 255, 255, 0.04) !important;
    }
  }
  
  td {
    padding: 0 16px !important;
    height: 48px;
    white-space: nowrap;
    border-bottom: thin solid rgba(255, 255, 255, 0.12) !important;
    color: #fff !important;
  }

  tbody {
    color: #fff !important;
    
    tr {
      color: #fff !important;
      
      td {
        color: #fff !important;
      }
    }
  }

  .v-table__wrapper {
    color: #fff !important;
  }

  .v-data-table__wrapper {
    color: #fff !important;
  }

  .v-btn {
    text-transform: none !important;
    letter-spacing: 0 !important;
    font-weight: 400 !important;
    height: 32px !important;
    min-width: 70px !important;
  }

  .v-data-table-footer {
    background-color: #2B2D33 !important;
    color: #fff !important;
    border-top: thin solid rgba(255, 255, 255, 0.12);
    min-height: 56px;
    padding: 0 16px;
    display: flex;
    align-items: center;
    justify-content: flex-end;

    .v-data-table-footer__pagination {
      color: rgba(255, 255, 255, 0.7) !important;
      margin-right: 16px;
    }

    .v-btn {
      min-width: 36px !important;
      width: 36px;
      padding: 0 !important;
      margin: 0 4px;

      &:not(.v-btn--disabled) {
        background: var(--primary-gradient) !important;
        color: white !important;
        border: none !important;
        box-shadow: 0 2px 8px rgba(227, 76, 101, 0.3) !important;

        &:hover {
          box-shadow: 0 4px 12px rgba(227, 76, 101, 0.4) !important;
        }
        
        &:active {
          box-shadow: 0 1px 4px rgba(227, 76, 101, 0.2) !important;
        }
      }
      
      &.v-btn--disabled {
        opacity: 0.5;
        background: linear-gradient(315deg, rgba(227, 76, 101, 0.5), rgba(255, 123, 142, 0.5)) !important;
        color: rgba(255, 255, 255, 0.5) !important;
        box-shadow: none !important;
      }
    }

    .v-data-table-footer__items-per-page,
    .v-data-table-footer__info {
      color: rgba(255, 255, 255, 0.7);
    }
  }
}

:deep(.v-card) {
  box-shadow: none !important;
  border: 1px solid rgba(255, 255, 255, 0.12) !important;
}

.review-dialog {
  background-color: #2B2D33 !important;
  
  .v-card-title {
    color: #fff !important;
    padding: 16px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);
    font-size: 18px;
  }

  .v-card-text {
    padding: 24px;
    color: #fff !important;
  }

  .review-content {
    text-align: center;
  }

  .review-buttons {
    display: flex;
    justify-content: center;
    gap: 24px;
    margin-top: 8px;
  }

  .review-action-btn {
    min-width: 120px;
    height: 40px !important;
    font-weight: 400;
    text-transform: none;
    letter-spacing: 0;
    font-size: 16px;
    color: #fff !important;
  }

  .approve-btn {
    background-color: #1976D2 !important;
    
    &:hover {
      background-color: #1565C0 !important;
    }
  }
  
  .reject-btn {
    background-color: #F44336 !important;
    
    &:hover {
      background-color: #E53935 !important;
    }
  }
}

:deep(.date-input) {
  /* 日期选择器按钮样式 */
  .v-field__append-inner button {
    color: #fff !important;
  }
  
  /* 日期选择器图标样式 */
  .v-field__append-inner i {
    color: #fff !important;
  }
  
  /* 日期选择器下拉菜单样式 */
  .v-date-picker {
    background-color: #2B2D33 !important;
    color: #fff !important;
    
    .v-date-picker-header {
      color: #fff !important;
    }
    
    .v-date-picker-table {
      color: #fff !important;
    }
  }
}
</style> 